import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'
import { getFormValues, isDirty as isDirtyForm } from 'redux-form'

import { UPLOAD_DIRECTORY } from 'services/legacy/upload/constants'
import { MEDICATION_FORM_STATUSES, MF_SIGNATURE_TYPES } from 'services/medicationForms/constants'
import { Signature } from 'services/medicationForms/models'

import { generateRoute } from 'utils/routing'
import { createTypeFromEnumValues } from 'utils/typescript'

import { withAppService, withAppServiceProps } from 'services/app'
import { withChildService, withChildServiceProps } from 'services/legacy/child'
import { withUploadService, withUploadServiceProps } from 'services/legacy/upload'
import { withMedicationFormsService, withMedicationFormsServiceProps } from 'services/medicationForms'
import { isLongTermMedicationForm } from 'services/medicationForms/single/selectors'
import { withNurseryContextService, withNurseryContextServiceProps } from 'services/nurseryContext'
import { withRouter, withRouterProps } from 'services/router'
import { withModalService, withModalServiceProps } from 'services/utils/modal'

import { ModalType } from 'modals'

import { RootState } from 'core/reducers'
import i18n from 'translations'

import AddView from './AddView'
import { ADD_MEDICATION_FORM, AddMedicationFormVales } from './components/AddForm'

const MEDICATION_FORMS_GROUPS = {
  read: [
    'medicationForm',
    'medicationForm.medicationDetails',
    'medicationForm:medicationDetails',
    'medicationDetails',
    'medicationForm.child',
    'medicationForm.signatures',
    'medicationFormSignature',
    'medicationFormSignature.signature',
    'medicationFormSignature.owner',
    'child',
  ],
}

type AddContainerProps =
  & withAppServiceProps
  & withChildServiceProps
  & withMedicationFormsServiceProps
  & withModalServiceProps
  & withNurseryContextServiceProps
  & withRouterProps
  & withUploadServiceProps

const mapState = (state: RootState, {
  appSelectors,
  childSelectors,
  medicationFormsSingleState,
  nurseryContextSelectors,
}: AddContainerProps) => ({
  child: childSelectors.getChildSelector(state),
  errorMessages: appSelectors.getErrorMessages(medicationFormsSingleState),
  formValues: getFormValues(ADD_MEDICATION_FORM)(state) as AddMedicationFormVales,
  isDirty: isDirtyForm(ADD_MEDICATION_FORM)(state),
  isFetching: appSelectors.getIsFetching(medicationFormsSingleState),
  nursery: nurseryContextSelectors.getNurseryContext(state),
})

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

const AddContainer: React.FC<AddContainerProps & PropsFromRedux> = ({
  child,
  errorMessages,
  formValues,
  isDirty,
  isFetching,
  medicationFormsActions,
  medicationFormsSelectors,
  modalActions,
  navigate,
  nursery,
  params,
  router,
  uploadActions,
}) => {
  const { childId, id: medicationFormId } = params || {}

  const isChildContext = !!childId

  const [initialValues, setInitialValues] = useState<AddMedicationFormVales>()
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isDraftSubmitting, setIsDraftSubmitting] = useState<boolean>(false)

  const disableUnsavedPopup = useRef<boolean>(false)

  const startDate = formValues?.startDate
  const endDate = formValues?.endDate

  const isLongTerm = useMemo(() => isLongTermMedicationForm(startDate, endDate), [startDate, endDate])

  useEffect(() => {
    if (medicationFormId) {
      medicationFormsActions.get({
        onSuccess: ({ data }) => setInitialValues(medicationFormsSelectors.getInitialValues(data)),
        params: [medicationFormId, {
          groups: MEDICATION_FORMS_GROUPS,
        }],
      })
    } else {
      setInitialValues(medicationFormsSelectors.getInitialValues(null, child))
    }

    return () => {
      medicationFormsActions.clear()
    }
  }, [])

  const handleUnsavedPopupSuccess = (nextLocation) => {
    disableUnsavedPopup.current = true
    navigate(nextLocation)

    return true
  }

  const handleUnsavedPopup = (nextLocation) => {
    if (disableUnsavedPopup.current) {
      return null
    }

    if (isDirty) {
      modalActions.show<ModalType.CONFIRM>(ModalType.CONFIRM, {
        cancelButtonLabel: i18n.t('global:Continue'),
        confirmButtonLabel: i18n.t('global:Discard'),
        icon: 'warning',
        onConfirm: () => handleUnsavedPopupSuccess(nextLocation),
        text: i18n.t('module:Safeguarding:MedicationForm:Add:leavePopupMessage'),
      })
    }

    return !isDirty
  }

  router.setRouteLeaveHook(router.routes[router.routes.length - 1], handleUnsavedPopup)

  const redirectToList = () => {
    if (isChildContext) {
      return navigate(generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.LIST', { childId }))
    }

    return navigate(generateRoute('SAFEGUARDING.MEDICATION_FORM.LIST'))
  }

  const handleSubmitForm = async (
    fields: AddMedicationFormVales,
    status?: createTypeFromEnumValues<MEDICATION_FORM_STATUSES>,
  ) => {
    const {
      parent,
      parentSignature,
      staffReceivingMedication,
      staffReceivingMedicationSignature,
    } = fields

    const signatureBody: Signature[] = []

    if (status !== MEDICATION_FORM_STATUSES.DRAFT) {
      setIsSubmitting(true)

      if (parent && parentSignature && !parentSignature?.isUploaded) {
        signatureBody.push({
          carer: {
            id: parent.value as number,
          },
          signature: {
            mimeType: parentSignature?.file?.type,
            size: parentSignature?.file?.size,
            url: await uploadActions.uploadFile(
              parentSignature?.file,
              parentSignature?.file.name,
              UPLOAD_DIRECTORY.IMAGES,
            ) as any,
          },
          type: MF_SIGNATURE_TYPES.PARENT,
        })
      }

      if (
        staffReceivingMedication
        && staffReceivingMedicationSignature
        && !staffReceivingMedicationSignature?.isUploaded
      ) {
        signatureBody.push({
          owner: {
            id: staffReceivingMedication.value as number,
          },
          signature: {
            mimeType: staffReceivingMedicationSignature?.file?.type,
            size: staffReceivingMedicationSignature?.file?.size,
            url: await uploadActions.uploadFile(
              staffReceivingMedicationSignature?.file,
              staffReceivingMedicationSignature?.file.name,
              UPLOAD_DIRECTORY.IMAGES,
            ) as any,
          },
          type: MF_SIGNATURE_TYPES.STAFF,
        })
      }
    } else {
      setIsDraftSubmitting(true)
    }

    const body = medicationFormsSelectors.getBody(fields, status, isLongTerm)

    if (medicationFormId) {
      medicationFormsActions.update({
        body: {
          ...body,
          signatures: signatureBody?.length ? signatureBody : undefined,
        },
        onFailed: () => {
          setIsSubmitting(false)
          setIsDraftSubmitting(false)
        },
        onSuccess: () => {
          disableUnsavedPopup.current = true
          redirectToList()
          setIsSubmitting(false)
          setIsDraftSubmitting(false)
        },
        params: medicationFormId,
      })

      return
    }

    medicationFormsActions.create({
      body: {
        ...body,
        signatures: signatureBody?.length ? signatureBody : undefined,
      },
      onFailed: () => {
        setIsSubmitting(false)
        setIsDraftSubmitting(false)
      },
      onSuccess: () => {
        disableUnsavedPopup.current = true
        redirectToList()
        setIsSubmitting(false)
        setIsDraftSubmitting(false)
      },
      params: [{}],
    })
  }

  const handleSubmit = (fields: AddMedicationFormVales) => {
    handleSubmitForm(fields)
  }

  const handleSaveAsDraft = () => {
    handleSubmitForm(formValues, MEDICATION_FORM_STATUSES.DRAFT)
  }

  const handleDelete = () => {
    medicationFormsActions.remove({
      onSuccess: redirectToList,
      params: [medicationFormId],
    })
  }

  const handleDeleteClick = () => {
    modalActions.show<ModalType.CONFIRM>(ModalType.CONFIRM, {
      confirmButtonLabel: i18n.t('global:Delete'),
      icon: 'trash',
      onConfirm: handleDelete,
      text: i18n.t('module:Safeguarding:MedicationForm:Add:deleteText'),
    })
  }

  return (
    <AddView
      childId={childId}
      errorMessages={errorMessages}
      initialValues={initialValues}
      isChildContext={isChildContext}
      isDraftSubmitting={isDraftSubmitting}
      isEdit={!!medicationFormId}
      isLoading={isFetching}
      isLongTerm={isLongTerm}
      isSubmitting={isSubmitting}
      nursery={nursery}
      onDeleteClick={handleDeleteClick}
      onSubmit={handleSubmit}
      onSubmitAsDraft={handleSaveAsDraft}
    />
  )
}

const enhance = compose(
  withAppService,
  withChildService,
  withMedicationFormsService,
  withModalService,
  withNurseryContextService,
  withUploadService,
  withRouter,
  connector,
)

export default enhance(AddContainer)
